import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface ClaudeDesktopSetupProps {
  latestApiKey: string | null;
  onApiKeyUsed?: () => void;
  onQuickGenerate?: () => void;
  isGeneratingKey?: boolean;
  quickGenerateError?: string | null;
}

const ClaudeDesktopSetup: React.FC<ClaudeDesktopSetupProps> = ({
  latestApiKey,
  onApiKeyUsed,
  onQuickGenerate,
  isGeneratingKey,
  quickGenerateError,
}) => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [selectedOS, setSelectedOS] = useState<"mac" | "windows">("mac");
  const [showAutoFillMessage, setShowAutoFillMessage] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(
    "prerequisites",
  );

  // Detect OS
  useEffect(() => {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes("win")) {
      setSelectedOS("windows");
    }
  }, []);

  // Auto-populate API key when a new one is created
  useEffect(() => {
    if (latestApiKey) {
      setApiKey(latestApiKey);
      setShowConfig(true);
      setShowAutoFillMessage(true);
      setExpandedSection("install");
      // Scroll to this section
      setTimeout(() => {
        const element = document.getElementById("claude-desktop-setup");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      // Clear the auto-fill message after 5 seconds
      setTimeout(() => {
        setShowAutoFillMessage(false);
      }, 5000);
      // Notify parent that we've used the key
      if (onApiKeyUsed) {
        setTimeout(onApiKeyUsed, 10000); // Clear after 10 seconds
      }
    }
  }, [latestApiKey, onApiKeyUsed]);

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setApiKey(value);
    setShowConfig(value.startsWith("ao_"));
    if (value.startsWith("ao_")) {
      setExpandedSection("install");
    }
  };

  const generateConfig = () => {
    return JSON.stringify(
      {
        mcpServers: {
          "always-obvious": {
            command: "npx",
            args: ["@cormirus/always-obvious-mcp@latest"],
            env: {
              ALWAYS_OBVIOUS_API_KEY: apiKey,
            },
          },
        },
      },
      null,
      2,
    );
  };

  const copyConfig = () => {
    navigator.clipboard.writeText(generateConfig());
    setCopiedConfig(true);
    setTimeout(() => setCopiedConfig(false), 2000);
  };

  const getConfigPath = () => {
    if (selectedOS === "windows") {
      return "%APPDATA%\\Claude\\claude_desktop_config.json";
    }
    return "~/Library/Application Support/Claude/claude_desktop_config.json";
  };

  const getManualInstructions = () => {
    if (selectedOS === "windows") {
      return (
        <ol className="list-decimal list-inside space-y-2 text-sm">
          <li>
            Press <kbd className="px-2 py-1 bg-gray-100 rounded">Win</kbd> +{" "}
            <kbd className="px-2 py-1 bg-gray-100 rounded">R</kbd>
          </li>
          <li>
            Type:{" "}
            <code className="px-2 py-1 bg-gray-100 rounded text-xs">
              %APPDATA%\Claude
            </code>
          </li>
          <li>Press Enter</li>
          <li>
            Open{" "}
            <code className="px-2 py-1 bg-gray-100 rounded text-xs">
              claude_desktop_config.json
            </code>{" "}
            in Notepad
          </li>
          <li>Replace entire contents with the configuration below</li>
          <li>Save and close the file</li>
          <li>Restart Claude Desktop</li>
        </ol>
      );
    }
    return (
      <ol className="list-decimal list-inside space-y-2 text-sm">
        <li>
          Open <strong>Finder</strong>
        </li>
        <li>
          Press <kbd className="px-2 py-1 bg-gray-100 rounded">Cmd</kbd> +{" "}
          <kbd className="px-2 py-1 bg-gray-100 rounded">Shift</kbd> +{" "}
          <kbd className="px-2 py-1 bg-gray-100 rounded">G</kbd>
        </li>
        <li>
          Paste:{" "}
          <code className="px-2 py-1 bg-gray-100 rounded text-xs">
            ~/Library/Application Support/Claude/
          </code>
        </li>
        <li>
          Open{" "}
          <code className="px-2 py-1 bg-gray-100 rounded text-xs">
            claude_desktop_config.json
          </code>{" "}
          in TextEdit
        </li>
        <li>Replace entire contents with the configuration below</li>
        <li>Save and close the file</li>
        <li>Restart Claude Desktop</li>
      </ol>
    );
  };

  return (
    <div id="claude-desktop-setup" className="p-6 max-w-4xl mx-auto">
      {/* Auto-fill success message */}
      {showAutoFillMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
          <span className="text-green-600">✨</span>
          <p className="text-green-800">
            Your new API key has been automatically filled below!
          </p>
        </div>
      )}

      {/* Platform Selection */}
      <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-3">
          Which platform are you setting up?
        </h3>
        <div className="flex gap-3">
          <button
            onClick={() => setSelectedOS("mac")}
            className={`flex-1 px-4 py-3 rounded-md border-2 transition ${
              selectedOS === "mac"
                ? "border-purple-500 bg-purple-100 text-purple-700"
                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-lg">🍎</span>
              <span className="font-medium">Mac</span>
            </div>
          </button>
          <button
            onClick={() => setSelectedOS("windows")}
            className={`flex-1 px-4 py-3 rounded-md border-2 transition ${
              selectedOS === "windows"
                ? "border-purple-500 bg-purple-100 text-purple-700"
                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-lg">🪟</span>
              <span className="font-medium">Windows</span>
            </div>
          </button>
        </div>
      </div>

      {/* Important Notice */}
      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <span className="text-amber-600 text-xl">⚠️</span>
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900 mb-1">
              One-Time Setup Required
            </h3>
            <p className="text-sm text-amber-800">
              This setup takes about 10 minutes and requires Node.js to be
              installed. Don&apos;t worry - we&apos;ll guide you through each
              step!
            </p>
          </div>
        </div>
      </div>

      {/* Single Column Setup Steps */}
      <div className="space-y-6">
        {/* Prerequisites - EXPANDED BY DEFAULT */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() =>
              setExpandedSection(
                expandedSection === "prerequisites" ? null : "prerequisites",
              )
            }
            className="w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition"
          >
            <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
              !
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-semibold text-gray-900 mb-1">
                Prerequisites Check
              </h3>
              <p className="text-sm text-gray-600">
                Node.js is usually already installed, but we&apos;ve seen a few
                cases where it was missing
              </p>
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${
                expandedSection === "prerequisites" ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {expandedSection === "prerequisites" && (
            <div className="px-4 pb-4 border-t border-gray-100">
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Why do I need Node.js?</strong> The AI assistant
                    runs as a small program on your computer that connects
                    Claude Desktop to Spotlight.
                  </p>
                </div>

                {/* Check if Node is installed */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Check if you already have Node.js:
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">
                      {selectedOS === "mac"
                        ? "1. Open Terminal (Cmd+Space, type 'Terminal')"
                        : "1. Open Command Prompt (Win+R, type 'cmd')"}
                    </p>
                    <p className="text-xs text-gray-600">
                      2. Type this command and press Enter:
                    </p>
                    <div className="bg-gray-900 text-gray-100 p-2 rounded text-xs font-mono">
                      node --version
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      ✅ If you see a version number (like v18.17.0),
                      you&apos;re all set!
                      <br />❌ If you see &quot;command not found&quot;, install
                      Node.js below.
                    </p>
                  </div>
                </div>

                {/* Install Node.js */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Install Node.js (if needed):
                  </h4>
                  <a
                    href="https://nodejs.org/en/download/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                      />
                    </svg>
                    Download Node.js (LTS Version)
                  </a>
                  <p className="text-xs text-gray-500 mt-2">
                    Download and run the installer. Use all default options -
                    just keep clicking &quot;Next&quot;!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 1: Download Claude */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                Get Claude Desktop
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Download and install Claude Desktop from Anthropic
              </p>
              <a
                href="https://claude.ai/download"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
                Download Claude Desktop
              </a>
            </div>
          </div>
        </div>

        {/* Step 2: Enter API Key - ALWAYS VISIBLE */}
        <div
          className={`bg-white rounded-lg border ${
            !apiKey ? "border-purple-400 shadow-sm" : "border-gray-200"
          } overflow-hidden`}
        >
          <div
            className={`p-4 ${!apiKey ? "bg-purple-50" : ""} transition-colors`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`flex-shrink-0 w-8 h-8 ${
                  !apiKey ? "bg-purple-600" : "bg-purple-100"
                } rounded-full flex items-center justify-center ${
                  !apiKey ? "text-white" : "text-purple-600"
                } font-bold transition-colors`}
              >
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Create and Enter Your API Key
                </h3>
                {!apiKey && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded space-y-2">
                    <p className="text-sm text-blue-800">
                      <strong>Option 1 (Recommended):</strong> Click the button
                      below to instantly generate an API key
                    </p>
                    <p className="text-sm text-blue-800">
                      <strong>Option 2:</strong> For custom naming or
                      management:
                    </p>
                    <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                      <li>
                        Go to{" "}
                        <button
                          onClick={() =>
                            navigate("/account", {
                              state: { activeTab: "api-keys" },
                            })
                          }
                          className="underline hover:text-blue-900 font-semibold"
                        >
                          Account Management → API Keys tab
                        </button>
                      </li>
                      <li>
                        Click &quot;Generate New Key&quot; to create a custom
                        named key
                      </li>
                      <li>Copy the key and paste it in the field below</li>
                    </ul>
                    {onQuickGenerate && (
                      <button
                        onClick={onQuickGenerate}
                        disabled={Boolean(isGeneratingKey)}
                        className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isGeneratingKey ? (
                          <>
                            <svg
                              className="w-4 h-4 animate-spin"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Generating...
                          </>
                        ) : (
                          <>
                            <span>⚡</span>
                            <span>Quick generate Always Obvious API key</span>
                          </>
                        )}
                      </button>
                    )}
                    {quickGenerateError && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                        {quickGenerateError}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-600 mb-3">
                  {apiKey
                    ? "✅ Your API key is ready!"
                    : "Paste your API key here to continue setup"}
                </p>
                <input
                  type="text"
                  value={apiKey}
                  onChange={handleApiKeyChange}
                  placeholder="Paste your API key here (starts with ao_...)"
                  className={`w-full px-3 py-2 border ${
                    !apiKey
                      ? "border-purple-300 bg-purple-50"
                      : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm`}
                />
                {apiKey && !apiKey.startsWith("ao_") && (
                  <p className="text-red-600 text-xs mt-1">
                    API key should start with &ldquo;ao_&rdquo;
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Quick Install (Mac) or Manual Config (Windows) */}
        {showConfig && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() =>
                setExpandedSection(
                  expandedSection === "install" ? null : "install",
                )
              }
              className="w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition"
            >
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
                3
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 mb-1">
                  {selectedOS === "mac"
                    ? "Run the Installer (Mac - Recommended)"
                    : "Configure Claude Desktop (Windows)"}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedOS === "mac"
                    ? "Automatic setup with one Terminal command"
                    : "Add the configuration to Claude Desktop"}
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  expandedSection === "install" ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSection === "install" && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-4 space-y-4">
                  {selectedOS === "mac" ? (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-sm text-blue-800 mb-2">
                          <strong>How to open Terminal:</strong>
                        </p>
                        <ol className="text-sm text-blue-800 space-y-1">
                          <li>
                            1. Press{" "}
                            <kbd className="px-2 py-1 bg-white rounded">
                              Cmd
                            </kbd>{" "}
                            +{" "}
                            <kbd className="px-2 py-1 bg-white rounded">
                              Space
                            </kbd>
                          </li>
                          <li>2. Type &quot;Terminal&quot;</li>
                          <li>3. Press Enter</li>
                        </ol>
                      </div>

                      <div>
                        <p className="text-sm text-gray-700 mb-2">
                          Copy and paste this command into Terminal:
                        </p>
                        <div className="bg-gray-900 text-gray-100 p-3 rounded text-xs font-mono break-all">
                          {apiKey
                            ? `bash -c "$(curl -fsSL ${window.location.origin}/claude-desktop/install-mcp.command)" -- "${apiKey}"`
                            : `bash -c "$(curl -fsSL ${window.location.origin}/claude-desktop/install-mcp.command)"`}
                        </div>
                        <button
                          onClick={() => {
                            const command = apiKey
                              ? `bash -c "$(curl -fsSL ${window.location.origin}/claude-desktop/install-mcp.command)" -- "${apiKey}"`
                              : `bash -c "$(curl -fsSL ${window.location.origin}/claude-desktop/install-mcp.command)`;
                            navigator.clipboard.writeText(command);
                            setCopiedCommand(true);
                            setTimeout(() => setCopiedCommand(false), 2000);
                          }}
                          className="mt-2 w-full px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                        >
                          {copiedCommand ? "Copied!" : "Copy Install Command"}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          Press Enter to run. The installer will configure
                          everything automatically.
                        </p>

                        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                          <p className="text-xs text-green-800">
                            <strong>What this does:</strong> Downloads our setup
                            script, creates the config file, and sets your API
                            key - all automatically!
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Instructions for Windows:
                        </p>
                        {getManualInstructions()}
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-medium text-gray-700">
                            Configuration to copy:
                          </label>
                          <button
                            onClick={copyConfig}
                            className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition"
                          >
                            {copiedConfig ? "Copied!" : "Copy"}
                          </button>
                        </div>
                        <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-x-auto">
                          <code>{generateConfig()}</code>
                        </pre>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Manual Configuration (Always visible as an optional step) */}
        {showConfig && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() =>
                setExpandedSection(
                  expandedSection === "manual" ? null : "manual",
                )
              }
              className="w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition"
            >
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-bold">
                4
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Manual Configuration{" "}
                  <span className="text-sm font-normal text-gray-500">
                    (Optional)
                  </span>
                </h3>
                <p className="text-sm text-gray-600">
                  Only if the installer didn&apos;t work or you prefer manual
                  setup
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  expandedSection === "manual" ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSection === "manual" && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-4 space-y-4">
                  {/* File Location */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Configuration file location:
                    </label>
                    <code className="block p-2 bg-gray-100 rounded text-xs break-all">
                      {getConfigPath()}
                    </code>
                  </div>

                  {/* Instructions */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      How to manually configure:
                    </label>
                    <div className="bg-gray-50 p-3 rounded">
                      {getManualInstructions()}
                    </div>
                  </div>

                  {/* Configuration JSON */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Configuration to paste:
                      </label>
                      <button
                        onClick={copyConfig}
                        className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition"
                      >
                        {copiedConfig ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-x-auto">
                      <code>{generateConfig()}</code>
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Final Step */}
        {showConfig && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">
              🎉 Final Step - Test Your Setup
            </h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-green-800">
              <li>Restart Claude Desktop (quit and reopen)</li>
              <li>
                In any chat, type:{" "}
                <strong>&ldquo;Connect to Always Obvious&rdquo;</strong>
              </li>
              <li>Select your organization when prompted</li>
              <li>Start using AI assistance with your data!</li>
            </ol>
          </div>
        )}

        {/* Claude Project Setup - NEW SECTION */}
        {showConfig && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() =>
                setExpandedSection(
                  expandedSection === "project" ? null : "project",
                )
              }
              className="w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition"
            >
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                🎯
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Setting Up a Claude Project{" "}
                  <span className="text-sm font-normal text-blue-600">
                    (Recommended)
                  </span>
                </h3>
                <p className="text-sm text-gray-600">
                  Create a dedicated project for better context and persistent
                  instructions
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  expandedSection === "project" ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSection === "project" && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-4 space-y-4">
                  {/* Why Use Projects */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      Why Use a Claude Project?
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>
                        ✓ <strong>Persistent Context:</strong> Claude remembers
                        your organization across sessions
                      </li>
                      <li>
                        ✓ <strong>Custom Instructions:</strong> Define how you
                        want Claude to help with your strategic work
                      </li>
                      <li>
                        ✓ <strong>Automatic Connection:</strong> No need to
                        reconnect every time
                      </li>
                    </ul>
                  </div>

                  {/* How to Create */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Creating Your Project:
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>In Claude Desktop, click &quot;Projects&quot;</li>
                      <li>
                        Click &quot;New Project&quot; and name it after your
                        organization
                      </li>
                      <li>
                        Add custom instructions using the template below
                      </li>
                    </ol>
                  </div>

                  {/* Template Instructions */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-gray-900">
                        Custom Instructions Template:
                      </h4>
                      <button
                        onClick={() => {
                          const template = `Always connect to [Your Organization Name] using the Always Obvious MCP.

You are a strategy expert, helping me think through what needs to get done and using the Always Obvious Platform as a way to capture decisions and make plans.

When I ask about strategic matters, help me:
- Break down complex goals into clear OKRs
- Identify key decisions that need to be made
- Capture important insights and context in the platform
- Connect the dots between segments, propositions, and market opportunities

Keep responses focused and actionable. Use the MCP tools to save important information so nothing is lost.`;
                          navigator.clipboard.writeText(template);
                          setCopiedConfig(true);
                          setTimeout(() => setCopiedConfig(false), 2000);
                        }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                      >
                        {copiedConfig ? "Copied!" : "Copy Template"}
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs whitespace-pre-wrap">
                      <code>
                        {`Always connect to [Your Organization Name] using the Always Obvious MCP.

You are a strategy expert, helping me think through what needs to get done and using the Always Obvious Platform as a way to capture decisions and make plans.

When I ask about strategic matters, help me:
- Break down complex goals into clear OKRs
- Identify key decisions that need to be made
- Capture important insights and context in the platform
- Connect the dots between segments, propositions, and market opportunities

Keep responses focused and actionable. Use the MCP tools to save important information so nothing is lost.`}
                      </code>
                    </pre>
                    <p className="text-xs text-gray-600 mt-2">
                      💡 Replace &quot;[Your Organization Name]&quot; with your
                      actual organization name
                    </p>
                  </div>

                  {/* Example */}
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <h4 className="font-semibold text-green-900 mb-2">
                      Example (Cormirus):
                    </h4>
                    <pre className="bg-green-900 text-green-100 p-2 rounded text-xs whitespace-pre-wrap">
                      <code>
                        {`Always connect to Cormirus using the Always-Obvious MCP.

You are a strategy expert, helping me think through what needs to get done and using the Always Obvious Platform as a way to capture decisions and make plans.`}
                      </code>
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Troubleshooting */}
        {showConfig && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">
              ⚠️ Troubleshooting Common Issues
            </h4>
            <div className="space-y-2 text-sm text-yellow-800">
              <div>
                <strong>&quot;Command not found&quot; in Terminal/CMD</strong>
                <p className="text-xs mt-1">
                  → Node.js isn&apos;t installed. See Prerequisites above.
                </p>
              </div>
              <div>
                <strong>&quot;Permission denied&quot; on Mac</strong>
                <p className="text-xs mt-1">
                  → Try adding &quot;sudo&quot; before the command
                </p>
              </div>
              <div>
                <strong>&quot;Can&apos;t connect to Always Obvious&quot;</strong>
                <p className="text-xs mt-1">
                  → Check your API key starts with &quot;ao_&quot; and is copied
                  correctly
                </p>
              </div>
              <div>
                <strong>Claude Desktop won&apos;t restart</strong>
                <p className="text-xs mt-1">
                  → Fully quit Claude (check system tray/menu bar), then reopen
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaudeDesktopSetup;
